import React, { useCallback, useEffect, useRef, useState } from "react"
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import AddIcon from "@mui/icons-material/Add"
import CategoryIcon from "@mui/icons-material/Category"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import apiClient from "../../services/apiClient"
import {
    listCategories,
    createCategory,
} from "../../services/categoriesService"

const thStyle = {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase" as const,
    color: "#6b7280",
    letterSpacing: "0.05em",
}

function formatMinor(minor: number | null | undefined) {
    const value = Number(minor ?? 0)
    return `${(value / 100).toFixed(2)} €`
}

function CategoriesAdminPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [companyId, setCompanyId] = useState("")
    const [categories, setCategories] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [includeArchived, setIncludeArchived] = useState(false)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(20)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [createOpen, setCreateOpen] = useState(false)
    const [newName, setNewName] = useState("")
    const [newDescription, setNewDescription] = useState("")
    const [newUnitPrice, setNewUnitPrice] = useState("")
    const [creating, setCreating] = useState(false)

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const list = res.companies || []
                if (list.length > 0) {
                    setCompanyId(list[0].id)
                }
            } catch (error) {
                console.error("Failed to load companies:", error)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery)
            setPage(0)
        }, 300)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchQuery])

    const loadCategories = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await listCategories(companyId, {
                q: debouncedSearch || undefined,
                includeArchived,
                page: page + 1,
                pageSize,
            })
            setCategories(result.categories || [])
            setTotal(result.total || 0)
        } catch (error) {
            console.error("Failed to load categories:", error)
        } finally {
            setLoading(false)
        }
    }, [companyId, debouncedSearch, includeArchived, page, pageSize])

    useEffect(() => {
        loadCategories()
    }, [loadCategories])

    const handleRowClick = (categoryId: string) => {
        navigate(`/admin/categories/${categoryId}`)
    }

    const handleCreateCategory = async () => {
        if (!newName.trim() || !companyId) return
        setCreating(true)
        try {
            const unitPriceParsed = newUnitPrice.trim()
                ? Math.round(parseFloat(newUnitPrice) * 100)
                : null
            await createCategory(companyId, {
                name: newName.trim(),
                description: newDescription.trim() || null,
                unit_price_minor: unitPriceParsed,
            })
            setCreateOpen(false)
            setNewName("")
            setNewDescription("")
            setNewUnitPrice("")
            loadCategories()
        } catch (error) {
            console.error("Failed to create category:", error)
        } finally {
            setCreating(false)
        }
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CategoryIcon sx={{ fontSize: 28, color: "#374151" }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {t("categoriesAdmin.title")}
                    </Typography>
                    <Chip
                        label={t("categoriesAdmin.count", { count: total })}
                        size="small"
                        sx={{
                            backgroundColor: "#f3f4f6",
                            fontWeight: 500,
                            fontSize: "0.8rem",
                        }}
                    />
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateOpen(true)}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                    }}
                >
                    {t("categoriesAdmin.addCategory")}
                </Button>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                }}
            >
                <TextField
                    placeholder={String(t("categoriesAdmin.searchPlaceholder"))}
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{ color: "#9ca3af", fontSize: 20 }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: "1 1 250px",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#f9fafb",
                        },
                    }}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeArchived}
                            onChange={(e) => {
                                setIncludeArchived(e.target.checked)
                                setPage(0)
                            }}
                            size="small"
                        />
                    }
                    label={
                        <Typography
                            sx={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}
                        >
                            {t("categoriesAdmin.includeArchived")}
                        </Typography>
                    }
                />
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    overflow: "hidden",
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            py: 8,
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                ) : categories.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            py: 8,
                            color: "#9ca3af",
                        }}
                    >
                        <CategoryIcon
                            sx={{ fontSize: 48, mb: 1, opacity: 0.5 }}
                        />
                        <Typography sx={{ fontWeight: 500 }}>
                            {t("categoriesAdmin.empty")}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                                    <TableCell sx={thStyle}>
                                        {t("categoriesAdmin.columns.name")}
                                    </TableCell>
                                    <TableCell sx={thStyle} align="right">
                                        {t("categoriesAdmin.columns.itemCount")}
                                    </TableCell>
                                    <TableCell sx={thStyle} align="right">
                                        {t("categoriesAdmin.columns.salePrice")}
                                    </TableCell>
                                    <TableCell sx={thStyle}>
                                        {t("categoriesAdmin.columns.status")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {categories.map((category) => (
                                    <TableRow
                                        key={category.id}
                                        hover
                                        onClick={() =>
                                            handleRowClick(category.id)
                                        }
                                        sx={{
                                            cursor: "pointer",
                                            "&:last-child td": {
                                                borderBottom: 0,
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.9rem",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {category.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                sx={{
                                                    fontSize: "0.85rem",
                                                    color: "#374151",
                                                }}
                                            >
                                                {Number(
                                                    category.items_count || 0
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                sx={{
                                                    fontSize: "0.85rem",
                                                    fontFamily: "monospace",
                                                    color: "#374151",
                                                }}
                                            >
                                                {formatMinor(
                                                    category.unit_price_minor
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    category.is_archived
                                                        ? t(
                                                              "categoriesAdmin.status.archived"
                                                          )
                                                        : t(
                                                              "categoriesAdmin.status.active"
                                                          )
                                                }
                                                size="small"
                                                color={
                                                    category.is_archived
                                                        ? "default"
                                                        : "success"
                                                }
                                                variant={
                                                    category.is_archived
                                                        ? "outlined"
                                                        : "filled"
                                                }
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.75rem",
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!loading && categories.length > 0 && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => {
                            setPageSize(parseInt(e.target.value, 10))
                            setPage(0)
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage={t("common.rowsPerPage")}
                        labelDisplayedRows={({ from, to, count }) =>
                            String(
                                t("common.paginationDisplayedRows", {
                                    from,
                                    to,
                                    count,
                                })
                            )
                        }
                        sx={{
                            borderTop: "1px solid #e5e7eb",
                            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                                {
                                    fontSize: "0.85rem",
                                    color: "#6b7280",
                                },
                        }}
                    />
                )}
            </Paper>

            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t("categoriesAdmin.createDialog.title")}
                </DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: "16px !important",
                    }}
                >
                    <TextField
                        label={String(
                            t("categoriesAdmin.createDialog.fields.name")
                        )}
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        size="small"
                        required
                        autoFocus
                    />
                    <TextField
                        label={String(
                            t("categoriesAdmin.createDialog.fields.description")
                        )}
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        size="small"
                        multiline
                        rows={2}
                    />
                    <TextField
                        label={String(
                            t("categoriesAdmin.createDialog.fields.unitPrice")
                        )}
                        value={newUnitPrice}
                        onChange={(e) => setNewUnitPrice(e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ step: "0.01", min: "0" }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setCreateOpen(false)}
                        disabled={creating}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateCategory}
                        disabled={!newName.trim() || creating}
                    >
                        {creating
                            ? t("categoriesAdmin.createDialog.creating")
                            : t("categoriesAdmin.createDialog.create")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default CategoriesAdminPage
